import React from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import { getCiReportCompetitorVulnerability } from "../../../api/competitiveIntelligence";
import { InferredOccupancy, categoryLabel } from "../../../utils/ciFormat";
import { PanelTakeaway, fmtRate, fmtPct } from "../shared";

// vulnerability strings have the form "HIGH - <reason>" — split on the first dash
// to render the bucket as a colored badge and the reason as supporting text.
// Bucket is uppercased so styling matches regardless of backend casing.
const VALID_BUCKETS = new Set(["HIGH", "MEDIUM", "LOW"]);
const parseVulnerability = (raw) => {
  if (!raw) return { bucket: "UNKNOWN", reason: "" };
  const dashIndex = raw.indexOf(" - ");
  const bucketRaw = (dashIndex === -1 ? raw : raw.slice(0, dashIndex)).trim().toUpperCase();
  const reason = dashIndex === -1 ? "" : raw.slice(dashIndex + 3).trim();
  return { bucket: VALID_BUCKETS.has(bucketRaw) ? bucketRaw : "UNKNOWN", reason };
};

// Colors map to user benefit, not vulnerability magnitude:
//   HIGH vulnerability = easy to take share → green (good for the user)
//   LOW vulnerability  = hard to take share → red   (bad for the user)
const BUCKET_STYLES = {
  HIGH: {
    badge: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
    border: "border-l-emerald-500",
  },
  MEDIUM: {
    badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    border: "border-l-amber-500",
  },
  LOW: {
    badge: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    border: "border-l-red-500",
  },
  UNKNOWN: {
    badge: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    border: "border-l-gray-400",
  },
};

const VulnerabilityCard = ({ row }) => {
  const { bucket, reason } = parseVulnerability(row.vulnerability);
  const style = BUCKET_STYLES[bucket] || BUCKET_STYLES.UNKNOWN;
  return (
    <div className={`rounded-lg border border-gray-200 dark:border-gray-800 border-l-[4px] ${style.border} bg-white dark:bg-gray-900 p-4`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate" title={row.competitor_name || ""}>{row.competitor_name || "—"}</h3>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide mt-0.5">{categoryLabel(row.category_code)}</div>
        </div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-wide font-semibold shrink-0 ${style.badge}`}>
          {bucket}
        </span>
      </div>
      {reason && (
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{reason}</p>
      )}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div>
          <div className="text-gray-500 dark:text-gray-400">Limited Avail.</div>
          <div className="font-semibold text-amber-600 dark:text-amber-400">{row.soldout_dates ?? 0} <span className="font-normal opacity-70">({fmtPct(row.soldout_pct)})</span></div>
        </div>
        <div>
          <div className="text-gray-500 dark:text-gray-400">Min Stay Rows</div>
          <div className="text-gray-900 dark:text-gray-100 font-semibold">{row.min_stay_rows ?? 0}</div>
        </div>
        <div>
          <div className="text-gray-500 dark:text-gray-400">Avg Rate</div>
          <div className="text-gray-900 dark:text-gray-100 font-semibold">{fmtRate(row.avg_price)}</div>
        </div>
        <div>
          <div className="text-gray-500 dark:text-gray-400">Avg Occupancy</div>
          <div className="text-gray-900 dark:text-gray-100 font-semibold"><InferredOccupancy value={row.avg_occupancy} /></div>
        </div>
      </div>
    </div>
  );
};

const CompetitorVulnerabilityBody = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No competitor vulnerability data yet." />;
  }
  return (
    <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
      {rows.map((row, index) => (
        <VulnerabilityCard key={`${row.competitor_name}-${index}`} row={row} />
      ))}
    </div>
  );
};

// Sort vulnerability buckets so HIGH comes first.
const BUCKET_RANK = { HIGH: 0, MEDIUM: 1, LOW: 2, UNKNOWN: 3 };

const CompetitorVulnerabilityPanel = ({ clientId, say }) => (
  <AnalysisPanel
    id={`competitor-vulnerability-${clientId}`}
    title="Competitor Vulnerability"
    tip="Identifies where each competitor is weak — frequently at limited availability, heavy min-stay restrictions, or premium-priced. HIGH vulnerability = a competitor losing demand to whoever has inventory."
    description="Where competitors are most exposed — frequent limited availability, restrictive min-stays, premium pricing."
    fetcher={() => getCiReportCompetitorVulnerability(clientId)}
    deps={[clientId]}
  >
    {({ data }) => {
      const rows = Array.isArray(data) ? data : [];
      const sorted = [...rows].sort((rowA, rowB) => {
        const rankA = BUCKET_RANK[parseVulnerability(rowA.vulnerability).bucket] ?? 99;
        const rankB = BUCKET_RANK[parseVulnerability(rowB.vulnerability).bucket] ?? 99;
        return rankA - rankB;
      });
      return (
        <>
          <PanelTakeaway panelKey="competitor_vulnerability" say={say} />
          <CompetitorVulnerabilityBody rows={sorted} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default CompetitorVulnerabilityPanel;
